/**
 * File: ContractAnalysisDashboard.jsx
 * Path: /src/components/solutions/ContractAnalysisDashboard.jsx
 * Description: Standalone dashboard visualization for the Contract Analysis AI solution
 * Key Features: Interactive contract analysis visualization with highlighted clauses and risk assessment
 */

import React from 'react';
import { motion } from 'framer-motion';

const ContractAnalysisDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-secondary-700 to-primary-700 p-6">
        {/* Circuit pattern */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" stroke="rgba(255,255,255,0.3)" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" stroke="rgba(255,255,255,0.4)" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" stroke="rgba(255,255,255,0.25)" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" stroke="rgba(255,255,255,0.3)" />
            </g>
            <g fill="rgba(255,255,255,0.5)">
              <circle cx="492.5" cy="224.5" r="4" />
              <circle cx="365.5" cy="196.5" r="4" />
              <circle cx="627.5" cy="358.5" r="4" />
              <circle cx="200.5" cy="144.5" r="4" />
              <circle cx="549.5" cy="421.5" r="4" />
            </g>
          </svg>
        </div>
        
        {/* Dashboard container with min-height to prevent cutting off */}
        <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 w-full text-white">
            {/* Interface Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-secondary-500 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl">Veritect Contract Analysis AI</h3>
              </div>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            
            {/* Contract Preview with Analysis */}
            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 mb-4 text-sm">
              <p className="mb-2">
                <span className="bg-green-500/20 rounded px-1.5 py-0.5 mr-1">7.2</span>
                The Term of this Agreement shall be for a period of one (1) year commencing on the Effective Date.
              </p>
              <p className="mb-2">
                <span className="bg-red-500/20 rounded px-1.5 py-0.5 mr-1">7.3</span>
                <span className="bg-red-400/20 px-0.5">This Agreement shall automatically renew for successive one (1) year periods unless either party provides written notice of non-renewal at least sixty (60) days prior to the end of the then-current term.</span>
              </p>
              <p className="mb-2">
                <span className="bg-yellow-500/20 rounded px-1.5 py-0.5 mr-1">7.4</span>
                <span className="bg-yellow-400/20 px-0.5">In the event of any material breach of this Agreement, the non-breaching party may terminate this Agreement upon thirty (30) days written notice</span> to the breaching party, provided that such breach is not cured within such thirty (30) day period.
              </p>
            </div>
            
            {/* Risk Assessment */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-xs text-white/60 mb-1">Risk Assessment</div>
                <div className="flex items-center">
                  <div className="w-2 h-8 bg-red-500 rounded-full mr-1.5"></div>
                  <div className="w-2 h-6 bg-yellow-500 rounded-full mr-1.5"></div>
                  <div className="w-2 h-4 bg-green-500 rounded-full mr-3"></div>
                  <div>
                    <div className="font-medium text-sm">Auto-renewal clause (7.3)</div>
                    <div className="text-xs text-white/70">High Risk - 60-day notice period</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-xs text-white/60 mb-1">Suggested Improvements</div>
                <ul className="text-xs space-y-1">
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 bg-white rounded-full mt-1 mr-1.5"></div>
                    <span>Extend notice period to 90 days</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 bg-white rounded-full mt-1 mr-1.5"></div>
                    <span>Add cap on renewal price increases</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 bg-white rounded-full mt-1 mr-1.5"></div>
                    <span>Clarify material breach definition</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Progress Indicators */}
            <div className="flex space-x-3">
              <div className="flex-1 bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">Analysis Progress</div>
                <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div className="w-4/5 h-1.5 bg-secondary-500 rounded-full"></div>
                </div>
              </div>
              <div className="flex-1 bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/60 mb-1">Clauses Analyzed</div>
                <div className="text-xs font-medium">24/30</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractAnalysisDashboard;